import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { Card, Spinner, useToast } from './ui';
import type { Language } from '../types';

const T = {
  en: {
    title: 'The store cupboard',
    intro:
      'Packs do not divide into recipes. When a shop leaves something over — five of the six tins, most of the bottle of hoisin — it goes in here, so the next list does not buy it again and the planner has a reason to use it up over the following week or two.',
    none: 'Nothing in here yet. It fills itself in as you finish shops, and you can add anything the app would not know about.',
    inferred: 'left over from a shop',
    manual: 'added by hand',
    since: (date: string) => `since ${date}`,
    add: 'Add something',
    search: 'Search ingredients…',
    amount: 'How much?',
    save: 'Add',
    remove: 'Remove',
    noMatch: 'No ingredient matches that.',
    added: (name: string) => `${name} is in the cupboard`,
    removed: 'Taken out of the cupboard',
    edit: 'Change the amount',
  },
  fr: {
    title: 'Le placard',
    intro:
      'Les paquets ne se divisent pas en recettes. Ce qu’une course laisse en trop — cinq boîtes sur six, presque toute la bouteille de hoisin — arrive ici, pour ne pas le racheter et pour que le planificateur ait une raison de l’utiliser dans la semaine ou deux qui suivent.',
    none: 'Rien pour le moment. Le placard se remplit tout seul à mesure que les courses sont terminées, et vous pouvez ajouter ce que l’application ne peut pas deviner.',
    inferred: 'reste d’une course',
    manual: 'ajouté à la main',
    since: (date: string) => `depuis le ${date}`,
    add: 'Ajouter',
    search: 'Rechercher un ingrédient…',
    amount: 'Quelle quantité ?',
    save: 'Ajouter',
    remove: 'Retirer',
    noMatch: 'Aucun ingrédient ne correspond.',
    added: (name: string) => `${name} est au placard`,
    removed: 'Retiré du placard',
    edit: 'Modifier la quantité',
  },
} as const;

interface PantryEntry {
  ingredientId: string;
  name: string;
  quantity: number;
  unit: string;
  source: 'inferred' | 'manual';
  addedOn: string;
}

/**
 * What is already in the house (§20).
 *
 * Every line is a belief, not a measurement — nobody weighs the jar — so all
 * of it is editable and removable. The app fills it in from what the packs
 * left over; the family corrects it.
 */
export function StoreCupboardCard({
  language,
  readOnly,
}: {
  language: Language;
  readOnly: boolean;
}) {
  const t = T[language];
  const toast = useToast();
  const [entries, setEntries] = useState<PantryEntry[] | null>(null);
  const [ingredients, setIngredients] = useState<
    Array<{ id: string; name: string; default_unit: string; cupboard_staple: boolean }>
  >([]);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [pantry, library] = await Promise.all([api.pantry(language), api.ingredients()]);
    setEntries(pantry);
    setIngredients(library);
  }, [language]);

  useEffect(() => {
    void load();
  }, [load]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const held = new Set((entries ?? []).map((e) => e.ingredientId));
    return ingredients
      .filter((i) => !i.cupboard_staple && !held.has(i.id) && i.name.toLowerCase().includes(q))
      .slice(0, 6);
  }, [query, ingredients, entries]);

  if (!entries) {
    return (
      <Card title={t.title}>
        <Spinner />
      </Card>
    );
  }

  const save = async (ingredientId: string, quantity: number, unit: string, name: string) => {
    setBusy(true);
    try {
      await api.setPantryItem(ingredientId, quantity, unit);
      await load();
      toast(quantity > 0 ? t.added(name) : t.removed);
      setQuery('');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (ingredientId: string) => {
    setBusy(true);
    try {
      await api.removePantryItem(ingredientId);
      await load();
      toast(t.removed);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t.title}>
      <p className="dim" style={{ marginBottom: 14 }}>
        {t.intro}
      </p>

      {entries.length === 0 && <p className="dim">{t.none}</p>}

      <div className="stack" style={{ gap: 2 }}>
        {entries.map((entry) => (
          <div key={entry.ingredientId} className="pantry-row">
            <span className="pantry-name">{entry.name}</span>
            <label className="pantry-amount">
              <input
                type="number"
                min={0}
                step="any"
                defaultValue={entry.quantity}
                disabled={readOnly || busy}
                aria-label={t.edit}
                onBlur={(event) => {
                  const value = Number(event.target.value);
                  if (value !== entry.quantity) {
                    void save(entry.ingredientId, value, entry.unit, entry.name);
                  }
                }}
              />
              <span className="dim">{entry.unit}</span>
            </label>
            <span className="pantry-note dim">
              {entry.source === 'manual' ? t.manual : t.inferred} · {t.since(entry.addedOn)}
            </span>
            {!readOnly && (
              <button
                type="button"
                className="btn small"
                disabled={busy}
                onClick={() => void remove(entry.ingredientId)}
              >
                {t.remove}
              </button>
            )}
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="row" style={{ gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <input
            type="search"
            value={query}
            placeholder={t.search}
            disabled={busy}
            style={{ flex: 1, minWidth: 200 }}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      )}

      {!readOnly && query.trim() && (
        <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {matches.map((option) => (
            <button
              key={option.id}
              className="btn small"
              disabled={busy}
              onClick={() => void save(option.id, 1, option.default_unit, option.name)}
            >
              + {option.name}
            </button>
          ))}
          {matches.length === 0 && <p className="dim">{t.noMatch}</p>}
        </div>
      )}
    </Card>
  );
}
